// DEPLOY: /var/www/woody.wtf/frontend/src/components/SignalChart.js
// Strategy Builder "Signal Chart" tab: intraday candles for one ticker with a
// triangle under the candle after each saved signal fired (where the switch
// executes; hollow = regime unchanged), plus a second synced
// chart of a held ticker with the last 1m backtest's fills. Data comes from
// GET /api/strategy/strategies/<id>/signal-chart (reads the SAVED strategy).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import SignalOutcomes, { nyToday, addDays, fmtNY } from './SignalOutcomes';

// Highcharts Stock loaded via CDN in public/index.html (same as the Chart page)
const HC = window.Highcharts;

const FRAMES   = ['1m','5m','10m','15m','30m','1h','2h','3h','4h'];
const MAX_DAYS = {'1m':10,'5m':30,'10m':60,'15m':90,'30m':180,'1h':365,'2h':365,'3h':365,'4h':365};   // mirrors SIGNAL_CHART_MAX_DAYS in strategy.py
const OVERLAYS = new Set(['ema08','ema09','ema12','ema20','ema21','ema26','ema50','ema70','ema100','ema150','ema200','bb_upper','bb_lower']);
const PANES    = [['rsi14',['rsi14']],['stoch',['stoch_k','stoch_d']],['obv',['obv']],['atr14',['atr14']]];
const LINE_COLORS = ['#2563EB','#D97706','#0891B2','#DB2777','#65A30D','#7C3AED'];

const PERIOD_MS = Object.fromEntries(FRAMES.map(f=>[f,parseInt(f)*(f.endsWith('h')?36e5:6e4)]));
const FILL_STYLE = {buy:['B','#16A34A'],sell:['S','#DC2626'],dca:['D','#2563EB']};
const isWeekend = iso => [0,6].includes(new Date(iso+'T12:00:00Z').getUTCDay());

// Highcharts Stock options for one signal-chart payload. fills: backtest trades
// for this ticker (minute timestamps), drawn as flags on the candle they filled in.
// all=false: only fires that switched the regime.
function chartOptions(data, ext, frame, fills, onRange, all) {
  const candles=data.candles.filter(c=>ext||!c[6]);
  if(!candles.length)return null;
  const keep=new Set(candles.map(c=>c[0]));
  const byX=Object.fromEntries(candles.map(c=>[c[0],c]));
  const ind=Object.fromEntries(Object.entries(data.indicators).map(([k,v])=>[k,v.filter(p=>keep.has(p[0]))]));
  const panes=PANES.filter(([,fs])=>fs.some(f=>ind[f]));
  // Pane heights in %: price on top, volume, then one pane per oscillator group.
  const oscH=16, volH=12, gap=2;
  const priceH=100-volH-gap-panes.length*(oscH+gap);
  const yAxis=[{height:priceH+'%',labels:{align:'left'},startOnTick:false,endOnTick:false},
               {top:(priceH+gap)+'%',height:volH+'%',offset:0,labels:{align:'left'}}];
  panes.forEach((_,i)=>yAxis.push({top:(priceH+gap+volH+gap+i*(oscH+gap))+'%',height:oscH+'%',offset:0,labels:{align:'left'}}));

  const series=[
    {type:'candlestick',id:'main',name:data.ticker,data:candles.map(c=>c.slice(0,5)),color:'#cc0000',upColor:'#00aa00',lineColor:'#000',upLineColor:'#000',
     tooltip:{pointFormatter(){const c=byX[this.x];return `O ${c[1]} H ${c[2]} L ${c[3]} C ${c[4]}<br/>Vol ${Math.round(c[5]).toLocaleString()}${c[6]?' (pre/post)':''}`;}}},
    {type:'column',id:'volume',name:'Volume',data:candles.map(c=>[c[0],c[5]]),yAxis:1,color:'#9CA3AF'},
  ];
  let li=0;
  Object.keys(ind).filter(k=>OVERLAYS.has(k)).forEach(k=>series.push({type:'line',name:k,data:ind[k],yAxis:0,lineWidth:1,color:LINE_COLORS[li++%LINE_COLORS.length]}));
  panes.forEach(([,fs],i)=>fs.filter(f=>ind[f]).forEach(f=>series.push({type:'line',name:f,data:ind[f],yAxis:2+i,lineWidth:1,color:LINE_COLORS[li++%LINE_COLORS.length]})));

  // One marker series per signal (legend click hides it). Triangles stack under
  // the candle low, spaced by the window's average candle range.
  const step=candles.reduce((a,c)=>a+(c[2]-c[3]),0)/candles.length*0.8||1;
  const stack={};
  const bySignal={};
  data.markers.filter(m=>keep.has(m.x)&&(all||m.regime_changed)).forEach(m=>{
    const n=stack[m.x]=(stack[m.x]||0)+1;
    (bySignal[m.signal_id]=bySignal[m.signal_id]||{m,pts:[]}).pts.push({x:m.x,y:byX[m.x][3]-n*step,m,
      marker:m.regime_changed?undefined:{fillColor:'#fff',lineColor:m.color,lineWidth:2}});
  });
  Object.values(bySignal).forEach(({m,pts})=>series.push({
    type:'scatter',name:m.label||'Signal',data:pts,yAxis:0,color:m.color||'#16A34A',
    marker:{symbol:'triangle',radius:6,lineColor:m.color||'#16A34A',lineWidth:1},
    tooltip:{pointFormatter(){const p=this.options.m;return `<b>${p.label||'Signal'}</b> → ${p.target_regime||''}<br/>Fired ${fmtNY(Date.parse(p.fired_at))} NY${p.conflict?'<br/>Conflict: regime held':p.regime_changed?'':'<br/>Regime already set'}`;}},
  }));

  // Fills: flag on the candle whose span contains the fill minute.
  const xs=candles.map(c=>c[0]);
  const flags={};
  (fills||[]).forEach(t=>{
    const ms=Date.parse(t.d);
    let lo=0,hi=xs.length-1,k=-1;
    while(lo<=hi){const mid=(lo+hi)>>1;if(xs[mid]<=ms){k=mid;lo=mid+1;}else hi=mid-1;}
    if(k<0||ms-xs[k]>=PERIOD_MS[frame])return;
    const [title,color]=FILL_STYLE[t.action]||['?','#6B7280'];
    (flags[t.action]=flags[t.action]||{title,color,pts:[]}).pts.push({x:xs[k],title,
      text:`<b>${t.action.toUpperCase()}</b> ${Number(t.shares).toFixed(2)} @ ${Number(t.price).toFixed(2)} ($${Number(t.value).toLocaleString('en-AU',{maximumFractionDigits:0})})<br/>Filled ${fmtNY(ms)} NY`});
  });
  Object.entries(flags).forEach(([action,{color,pts}])=>series.push({
    type:'flags',name:action==='buy'?'Buys':action==='sell'?'Sells':'DCA',data:pts,onSeries:'main',onKey:'high',
    shape:'squarepin',color,fillColor:color,style:{color:'#fff',fontSize:'10px'},y:-24,
  }));

  return {
    credits:{enabled:false},title:{text:''},rangeSelector:{enabled:false},
    time:{timezone:'America/New_York'},
    chart:{panning:{enabled:true,type:'x'},zooming:{type:'x'}},
    legend:{enabled:true,itemStyle:{fontSize:'11px'}},
    tooltip:{split:false,shared:false,xDateFormat:'%a %e %b %H:%M NY'},
    xAxis:{ordinal:true,crosshair:{width:1,color:'rgba(0,0,0,0.3)'},
           events:{afterSetExtremes(e){if(e.trigger)onRange(e.min,e.max);}}},   // user pan/zoom only
    yAxis,series,
    plotOptions:{series:{dataGrouping:{enabled:false}},scatter:{stickyTracking:false}},
  };
}

export default function SignalChart({activeId, sJson, saveStrategy, styles, trades, tradesAt, jump, onJumpDone}) {
  const {sec, sBody, lbl, inp, sel, btn, btnG} = styles;
  const tickers = [...new Set([
    ...sJson.signals.flatMap(s=>s.type==='smc'?[]:s.conditions.map(c=>c.ticker)),
    ...sJson.regime_levels.flatMap(l=>l.allocations.filter(a=>a.kind!=='rank').map(a=>a.ticker)),
  ].filter(t=>t&&t!=='CASH'))];
  const condFrames = sJson.signals.flatMap(s=>s.conditions.map(c=>c.timeframe)).filter(f=>FRAMES.includes(f));
  const held = [...new Set(sJson.regime_levels.flatMap(l=>l.allocations.filter(a=>a.kind!=='rank').map(a=>a.ticker)).filter(t=>t&&t!=='CASH'))];

  const [ticker,   setTicker]   = useState(tickers[0]||'');
  const [frame,    setFrame]    = useState(condFrames[0]||'5m');
  const [from,     setFrom]     = useState(()=>{let d=nyToday();while(isWeekend(d))d=addDays(d,-1);return d;});
  const [days,     setDays]     = useState(1);
  const [ext,      setExt]      = useState(true);
  const [all,      setAll]      = useState(false);   // every fire vs regime switches only
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [fillTk,   setFillTk]   = useState(held[0]||'');
  const [fillData, setFillData] = useState(null);
  const [fillErr,  setFillErr]  = useState('');
  const divRef   = useRef(null);
  const topRef   = useRef(null);
  const chartRef = useRef(null);
  const fillRef  = useRef(null);
  const fillChart= useRef(null);

  useEffect(()=>{ if(!tickers.includes(ticker)&&tickers[0])setTicker(tickers[0]); },[tickers.join(),ticker]); // eslint-disable-line react-hooks/exhaustive-deps

  const span = Math.min(Math.max(1,Number(days)||1), MAX_DAYS[frame]);

  // save=true: the engine reads the saved strategy, so edited signals must be saved first.
  const load = useCallback(async(fromD,save)=>{
    if(!activeId){setError('Save the strategy first.');return;}
    if(!ticker){setError('Add a signal condition or allocation ticker first.');return;}
    if(save&&!await saveStrategy())return;
    setLoading(true);setError('');
    const fetchOnce=async(tk,f)=>(await api.get(`/strategy/strategies/${activeId}/signal-chart`,
      {params:{ticker:tk,timeframe:frame,from:f,to:addDays(f,span-1)}})).data;
    // Fills chart uses the same window; same ticker reuses the signal chart's data.
    const loadFills=async(f,main)=>{
      setFillErr('');
      if(!fillTk){setFillData(null);return;}
      if(fillTk===ticker){setFillData(main);return;}
      try{ setFillData(await fetchOnce(fillTk,f)); }
      catch(e){ setFillData(null);setFillErr(e.response?.data?.error||'Fills chart failed.'); }
    };
    try{ const d=await fetchOnce(ticker,fromD); setData(d); await loadFills(fromD,d); }
    catch(e){
      const r=e.response?.data;
      // Empty window: jump once to the latest day that has candles.
      if(r?.latest&&r.latest!==fromD){
        const f=addDays(r.latest,-(span-1));
        setFrom(f);
        try{ const d=await fetchOnce(ticker,f); setData(d); await loadFills(f,d); }
        catch(e2){ setData(null);setFillData(null);setError(e2.response?.data?.error||'Load failed.'); }
      } else { setData(null);setFillData(null);setError(r?.error||'Load failed.'); }
    }
    finally{setLoading(false);}
  },[activeId,ticker,frame,span,saveStrategy,fillTk]);

  // Prev/next window; single-day windows skip weekends.
  const shift = dir => {
    let f=addDays(from,dir*span);
    while(span===1&&isWeekend(f))f=addDays(f,dir);
    setFrom(f); load(f,false);
  };

  // Outcome row clicked: chart that NY day (outcomes already saved the strategy).
  const pick = d => { setFrom(d); load(d,false); topRef.current?.scrollIntoView({behavior:'smooth'}); };

  // "Chart this day" from the Backtest tab.
  useEffect(()=>{ if(jump){ setFrom(jump.date); load(jump.date,false); onJumpDone(); } },[jump]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the two charts on the same time range when either is panned/zoomed.
  const syncTo = other => (min,max) => other.current?.xAxis[0].setExtremes(min,max,true,false);
  const fills = (trades||[]).filter(t=>t.ticker===fillTk&&String(t.d).length>10);

  // ── Build the Highcharts Stock charts ────────────────────────────────────
  useEffect(()=>{
    if(chartRef.current){chartRef.current.destroy();chartRef.current=null;}
    const opts=data&&divRef.current&&HC&&chartOptions(data,ext,frame,fillTk===ticker?fills:[],syncTo(fillChart),all);
    if(opts)chartRef.current=HC.stockChart(divRef.current,opts);
  },[data,ext,all]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    if(fillChart.current){fillChart.current.destroy();fillChart.current=null;}
    if(fillTk===ticker)return;   // fills already drawn on the signal chart
    const opts=fillData&&fillRef.current&&HC&&chartOptions(fillData,ext,frame,fills,syncTo(chartRef),all);
    if(opts)fillChart.current=HC.stockChart(fillRef.current,opts);
  },[fillData,ext,trades,all]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>()=>{[chartRef,fillChart].forEach(r=>{if(r.current){r.current.destroy();r.current=null;}});},[]);

  const shown=data?data.markers.filter(m=>(ext||!m.extended)&&(all||m.regime_changed)).length:0;
  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflowY:'auto',minHeight:0}}>
      <div ref={topRef} style={{...sec,marginBottom:'10px',flexShrink:0}}>
        <div style={sBody}>
          <div style={{display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap'}}>
            <div><label style={lbl}>Ticker</label>
              <select style={sel} value={ticker} onChange={e=>setTicker(e.target.value)}>
                {tickers.length===0&&<option value="">—</option>}
                {tickers.map(t=><option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><label style={lbl}>Candles</label>
              <select style={sel} value={frame} onChange={e=>setFrame(e.target.value)}>
                {FRAMES.map(f=><option key={f} value={f}>{f}</option>)}
              </select></div>
            <div><label style={lbl}>From (NY)</label>
              <input type="date" style={inp} value={from} onChange={e=>setFrom(e.target.value)}/></div>
            <div><label style={lbl}>Days (max {MAX_DAYS[frame]})</label>
              <input type="number" min="1" max={MAX_DAYS[frame]} style={{...inp,width:'60px'}} value={days} onChange={e=>setDays(e.target.value)}/></div>
            <label style={{...lbl,display:'flex',alignItems:'center',gap:'5px',cursor:'pointer',marginBottom:'6px'}}>
              <input type="checkbox" checked={ext} onChange={e=>setExt(e.target.checked)}/> Pre/post market
            </label>
            <label title="Off: regime-changing signals only. On: every signal fire, with hollow triangles for unchanged regimes." style={{...lbl,display:'flex',alignItems:'center',gap:'5px',cursor:'pointer',marginBottom:'6px'}}>
              <input type="checkbox" checked={all} onChange={e=>setAll(e.target.checked)}/> Every fire
            </label>
            <button style={btn} onClick={()=>load(from,true)} disabled={loading||!activeId} title="Saves the strategy first; the chart uses the saved signals">{loading?'Loading…':'Load chart'}</button>
            <button style={btnG} onClick={()=>shift(-1)} disabled={loading||!data} title="Previous window">◀</button>
            <button style={btnG} onClick={()=>shift(1)} disabled={loading||!data} title="Next window">▶</button>
          </div>
          <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'6px'}}>
            Times are New York. Triangles sit under the candle after the signal fired, at whose open the regime switches; hollow (Every fire on) = regime already set or conflicting signals. Signals are always evaluated on the full pre/post series — the toggle only hides those candles.
            {data&&` ${shown} firing${shown===1?'':'s'} shown.`}
            {data?.skipped_signals?.length>0&&` Not charted (SMC): ${data.skipped_signals.join(', ')}.`}
          </div>
          {error&&<div style={{fontSize:'11px',color:'#dc2626',marginTop:'6px'}}>{error}</div>}
        </div>
      </div>
      {!data&&!loading&&<div style={{padding:'30px 0',textAlign:'center',color:'var(--text-muted)',fontSize:'13px'}}>Pick a ticker and load the chart.</div>}
      <div ref={divRef} style={{flexShrink:0,height:'min(70vh,640px)',minHeight:'420px',display:data?'block':'none',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)'}}/>
      {held.length>0&&(
        <div style={{...sec,marginTop:'10px',marginBottom:0,flexShrink:0}}>
          <div style={{...sBody,display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap'}}>
            <div><label style={lbl}>Fills chart</label>
              <select style={sel} value={fillTk} onChange={e=>setFillTk(e.target.value)}>
                {held.map(t=><option key={t} value={t}>{t}</option>)}
              </select></div>
            <div style={{fontSize:'10px',color:'var(--text-muted)',flex:1,minWidth:'200px'}}>
              {trades
                ? `B / S / D flags = buys, sells and DCA from the last 1-minute backtest${tradesAt?` (run ${new Date(tradesAt).toLocaleString('en-AU')})`:''}, on the candle each filled in. Rerun the backtest after editing the strategy. Pans and zooms stay in sync with the chart above${fillTk===ticker?' (same ticker: fills are drawn on the chart above)':''}. Press Load chart after changing ticker.`
                : 'Run a 1-minute backtest to see its fills here.'}
            </div>
          </div>
          {fillErr&&<div style={{fontSize:'11px',color:'#dc2626',padding:'0 12px 8px'}}>{fillErr}</div>}
        </div>
      )}
      <div ref={fillRef} style={{flexShrink:0,height:'min(55vh,480px)',minHeight:'360px',marginTop:'10px',display:fillData&&fillTk!==ticker?'block':'none',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)'}}/>
      <SignalOutcomes activeId={activeId} saveStrategy={saveStrategy} ext={ext} onPick={pick} styles={styles}/>
    </div>
  );
}
